import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { BookHeart, Calendar, Sparkles, Users, User, Heart, Plus, Trash2, BookmarkPlus, Check } from 'lucide-react';
import { toast } from 'sonner';

export const CopingLibrary = () => {
  const { API, token } = useAuth();
  const [reflections, setReflections] = useState([]);
  const [personalStrategies, setPersonalStrategies] = useState([]);
  const [communityStrategies, setCommunityStrategies] = useState([]);
  const [savedCommunityIds, setSavedCommunityIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newStrategy, setNewStrategy] = useState('');
  const [newCategory, setNewCategory] = useState('self-care');
  const [showAddForm, setShowAddForm] = useState(false);

  const categories = [
    { value: 'grounding', label: 'Grounding' },
    { value: 'self-care', label: 'Self-Care' },
    { value: 'child-support', label: 'Child Support' },
    { value: 'community', label: 'Community' },
    { value: 'medical', label: 'Medical' }
  ];

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [reflectionsRes, libraryRes] = await Promise.all([
        axios.get(`${API}/weekly-reflection/history`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/coping-library`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setReflections(reflectionsRes.data);
      setPersonalStrategies(libraryRes.data.personal);
      setCommunityStrategies(libraryRes.data.community);
      setSavedCommunityIds(libraryRes.data.saved_community_ids || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const addPersonalStrategy = async () => {
    if (!newStrategy.trim()) {
      toast.error('Please enter a strategy');
      return;
    }

    try {
      await axios.post(`${API}/saved-coping`, {
        strategy: newStrategy,
        category: newCategory,
        source: 'personal'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Strategy added to your library!');
      setNewStrategy('');
      setShowAddForm(false);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to add strategy');
    }
  };

  const saveCommunityStrategy = async (strategy) => {
    try {
      await axios.post(`${API}/saved-coping`, {
        strategy: strategy.strategy,
        category: strategy.category,
        source: 'community',
        original_id: strategy.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Saved to your library!');
      setSavedCommunityIds(prev => [...prev, strategy.id]);
    } catch (error) {
      toast.error('Failed to save strategy');
    }
  };

  const deleteStrategy = async (strategyId) => {
    try {
      await axios.delete(`${API}/saved-coping/${strategyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Strategy removed');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete strategy');
    }
  };

  const markHelpful = async (strategyId) => {
    try {
      await axios.post(`${API}/shared-coping/${strategyId}/helpful`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Marked as helpful!');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to mark as helpful');
    }
  };

  const formatWeekRange = (weekStart) => {
    const start = new Date(weekStart);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  const getCategoryLabel = (value) => {
    return categories.find(c => c.value === value)?.label || value;
  };

  return (
    <div className="space-y-8" data-testid="coping-library">
      {/* Header */}
      <div className="bg-gradient-to-br from-primary/10 to-secondary/20 rounded-xl p-8 md:p-12">
        <div className="flex items-center gap-4 mb-4">
          <BookHeart className="w-12 h-12 text-primary" strokeWidth={1.5} />
          <h1 className="text-3xl md:text-4xl font-nunito font-bold text-[#2D2D2D]">
            Coping Library
          </h1>
        </div>
        <p className="text-lg text-[#57534E] leading-relaxed">
          Your personal collection of strategies combined with wisdom from the community. Build your toolkit for tough days.
        </p>
      </div>

      <Tabs defaultValue="personal" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3 bg-white rounded-xl border border-stone-200 p-2">
          <TabsTrigger value="personal" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white">
            <User className="w-4 h-4 mr-2" />My Strategies
          </TabsTrigger>
          <TabsTrigger value="community" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white">
            <Users className="w-4 h-4 mr-2" />Community
          </TabsTrigger>
          <TabsTrigger value="reflections" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white">
            <Calendar className="w-4 h-4 mr-2" />Weekly Reflections
          </TabsTrigger>
        </TabsList>

        {/* Personal Strategies */}
        <TabsContent value="personal" className="space-y-6">
          {/* Add New Strategy */}
          <Card className="bg-white rounded-xl border border-stone-100 p-6">
            {!showAddForm ? (
              <Button
                onClick={() => setShowAddForm(true)}
                className="w-full bg-primary/10 text-primary hover:bg-primary/20 rounded-lg py-4 flex items-center justify-center gap-2"
                data-testid="add-strategy-button"
              >
                <Plus className="w-5 h-5" />
                Add a Personal Strategy
              </Button>
            ) : (
              <div className="space-y-4">
                <Textarea
                  value={newStrategy}
                  onChange={(e) => setNewStrategy(e.target.value)}
                  placeholder="What helps you cope? Share a strategy that works for you..."
                  rows={3}
                  className="w-full"
                  data-testid="new-strategy-input"
                />
                <div className="flex flex-wrap gap-3 items-center">
                  <select
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    className="border border-stone-200 rounded-lg px-4 py-2 bg-white"
                    data-testid="category-select"
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                  <Button
                    onClick={addPersonalStrategy}
                    className="bg-primary text-white hover:bg-primary/90 rounded-full px-6"
                    data-testid="save-strategy-button"
                  >
                    Save Strategy
                  </Button>
                  <Button
                    onClick={() => {
                      setShowAddForm(false);
                      setNewStrategy('');
                    }}
                    variant="ghost"
                    className="rounded-full"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </Card>

          {/* Personal Strategies List */}
          {loading ? (
            <div className="text-center py-12">
              <p className="text-[#A8A29E]">Loading your strategies...</p>
            </div>
          ) : personalStrategies.length === 0 ? (
            <Card className="bg-white rounded-xl border border-stone-100 p-12 text-center">
              <Sparkles className="w-16 h-16 text-[#A8A29E] mx-auto mb-4" />
              <p className="text-[#57534E] text-lg mb-2">Your personal library is empty</p>
              <p className="text-[#A8A29E]">
                Add strategies that work for you, or save helpful ones from the community.
              </p>
            </Card>
          ) : (
            <div className="space-y-4">
              {personalStrategies.map((strategy) => (
                <Card
                  key={strategy.id}
                  className="bg-white rounded-xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-6"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="text-[#2D2D2D] leading-relaxed mb-3">{strategy.strategy}</p>
                      <div className="flex items-center gap-3">
                        <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
                          {getCategoryLabel(strategy.category)}
                        </span>
                        {strategy.source === 'community' && (
                          <span className="text-xs text-[#A8A29E]">Saved from community</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => deleteStrategy(strategy.id)}
                      className="p-2 text-[#A8A29E] hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      data-testid={`delete-strategy-${strategy.id}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Community Strategies */}
        <TabsContent value="community" className="space-y-6">
          <Card className="bg-secondary/20 rounded-xl border border-secondary p-4">
            <p className="text-[#57534E]">
              <span className="font-semibold text-secondary-foreground">Community Wisdom:</span> These strategies are shared anonymously by other PANDAS/PANS parents. Save the ones that resonate with you.
            </p>
          </Card>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-[#A8A29E]">Loading community strategies...</p>
            </div>
          ) : communityStrategies.length === 0 ? (
            <Card className="bg-white rounded-xl border border-stone-100 p-12 text-center">
              <Users className="w-16 h-16 text-[#A8A29E] mx-auto mb-4" />
              <p className="text-[#57534E] text-lg mb-2">No community strategies yet</p>
              <p className="text-[#A8A29E]">
                Be the first to share what helps you on the "What Helped" page.
              </p>
            </Card>
          ) : (
            <div className="space-y-4">
              {communityStrategies.map((strategy) => (
                <Card
                  key={strategy.id}
                  className="bg-white rounded-xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-6"
                >
                  <div className="space-y-4">
                    <p className="text-[#2D2D2D] leading-relaxed">{strategy.strategy}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="px-3 py-1 bg-secondary text-secondary-foreground rounded-full text-sm">
                          {getCategoryLabel(strategy.category)}
                        </span>
                        <span className="text-sm text-[#A8A29E]">
                          {strategy.helpful_count} found this helpful
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => markHelpful(strategy.id)}
                          variant="ghost"
                          className="text-[#A8A29E] hover:text-primary hover:bg-primary/10 rounded-full"
                          data-testid={`helpful-${strategy.id}`}
                        >
                          <Heart className="w-5 h-5 mr-1" />
                          Helpful
                        </Button>
                        {savedCommunityIds.includes(strategy.id) ? (
                          <span className="flex items-center gap-1 text-primary text-sm px-3 py-1.5 bg-primary/10 rounded-full">
                            <Check className="w-4 h-4" />
                            Saved
                          </span>
                        ) : (
                          <Button
                            onClick={() => saveCommunityStrategy(strategy)}
                            variant="ghost"
                            className="text-primary hover:bg-primary/10 rounded-full"
                            data-testid={`save-community-${strategy.id}`}
                          >
                            <BookmarkPlus className="w-5 h-5 mr-1" />
                            Save
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Weekly Reflections */}
        <TabsContent value="reflections" className="space-y-6">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-[#A8A29E]">Loading your reflections...</p>
            </div>
          ) : reflections.length === 0 ? (
            <Card className="bg-white rounded-xl border border-stone-100 p-12 text-center">
              <Calendar className="w-16 h-16 text-[#A8A29E] mx-auto mb-4" />
              <p className="text-[#57534E] text-lg mb-4">Your weekly reflections will appear here</p>
              <p className="text-[#A8A29E]">
                On Sundays, you'll be invited to reflect on what helped you that week.
              </p>
            </Card>
          ) : (
            <div className="space-y-6">
              {reflections.map((reflection) => (
                <Card
                  key={reflection.id}
                  className="bg-white rounded-xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-8"
                >
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 text-[#A8A29E]">
                      <Calendar className="w-5 h-5" />
                      <span className="text-sm font-medium">
                        Week of {formatWeekRange(reflection.week_start)}
                      </span>
                    </div>
                    <div className="border-l-4 border-primary pl-6">
                      <p className="text-[#2D2D2D] leading-relaxed whitespace-pre-wrap">
                        {reflection.coping_strategies}
                      </p>
                    </div>
                  </div>
                </Card>
              ))}

              <div className="text-center pt-6">
                <p className="text-sm text-[#A8A29E] italic">
                  You have {reflections.length} {reflections.length === 1 ? 'reflection' : 'reflections'} saved.
                </p>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CopingLibrary;
